// Shared validation for Company.dynamic_fields / Reply.dynamic_answers.
// Mirrors backend rules in talentapp/serializers.py::validate_dynamic_answers_against_fields.

const DYNAMIC_FIELD_LANGUAGES = ['es', 'pt', 'en'];

const INT_RE = /^-?\d+$/;

// Returns the subset of dynamic_fields that apply to a reply with the given
// created_date (ISO string). Fields whose `effective_from` is after the
// reply's creation are filtered out — they were introduced afterwards and
// can't be retroactively required. Archived fields are always filtered out
// (they're hidden from new and existing reply forms, but kept in the raw
// `dynamic_fields` list for resolving labels on historical answers). A
// null/undefined created_date means "new reply".
export function fieldsApplicableToReply(fields, replyCreatedAt) {
    const safe = (Array.isArray(fields) ? fields : []).filter(f => f && !f.archived);
    if (!replyCreatedAt) return safe;
    const replyTs = Date.parse(replyCreatedAt);
    if (Number.isNaN(replyTs)) return safe;
    return safe.filter(f => {
        if (!f.effective_from) return true;
        const effTs = Date.parse(f.effective_from);
        return Number.isNaN(effTs) || replyTs >= effTs;
    });
}

// Resolve a dynamic field's label for the active language.
// `field.label` may be a plain string (single-language) or {es, pt, en} (multi-language).
// Falls back through the active language → es → pt → en → field.key.
export function getDynamicLabel(field, language) {
    if (!field) return '';
    const {label, key} = field;
    if (typeof label === 'string') return label;
    if (label && typeof label === 'object') {
        const lang = (language || '').toLowerCase();
        if (lang && label[lang]) return label[lang];
        for (const fallback of DYNAMIC_FIELD_LANGUAGES) {
            if (label[fallback]) return label[fallback];
        }
    }
    return key || '';
}

export function getDynamicOptionLabel(option, language) {
    if (!option) return '';
    const {label, value} = option;
    if (typeof label === 'string') return label;
    if (label && typeof label === 'object') {
        const lang = (language || '').toLowerCase();
        if (lang && label[lang]) return label[lang];
        for (const fallback of DYNAMIC_FIELD_LANGUAGES) {
            if (label[fallback]) return label[fallback];
        }
    }
    return value || '';
}

export function validateDynamicAnswers(fields, answers, t, language) {
    const tr = t || ((k) => k);
    const errors = {};
    const safeFields = Array.isArray(fields) ? fields : [];
    const safeAnswers = answers && typeof answers === 'object' ? answers : {};

    for (const f of safeFields) {
        const {key, type, required} = f;
        const raw = safeAnswers[key];
        const present = raw !== undefined && raw !== null && String(raw).trim() !== '';

        if (!present) {
            if (required) errors[key] = `${getDynamicLabel(f, language)} ${tr('is_mandatory')}`;
            continue;
        }

        if (type === 'text') {
            // any non-empty string is valid
        } else if (type === 'integer') {
            if (!INT_RE.test(String(raw).trim())) {
                errors[key] = tr('must_be_integer');
            }
        } else if (type === 'select') {
            const allowed = (Array.isArray(f.options) ? f.options : []).map(o => o && o.value);
            if (typeof raw !== 'string' || !allowed.includes(raw)) {
                errors[key] = tr('invalid_option');
            }
        }
    }

    return {valid: Object.keys(errors).length === 0, errors};
}

export function coerceDynamicAnswersForSubmit(fields, answers) {
    const out = {};
    const safeFields = Array.isArray(fields) ? fields : [];
    const safeAnswers = answers && typeof answers === 'object' ? answers : {};
    for (const f of safeFields) {
        const raw = safeAnswers[f.key];
        if (raw === undefined || raw === null || String(raw).trim() === '') continue;
        if (f.type === 'integer') {
            out[f.key] = parseInt(String(raw).trim(), 10);
        } else {
            out[f.key] = String(raw);
        }
    }
    return out;
}
